import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Switch,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { empleadosApi } from "src/services/empleadosApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 24,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },
  header: {
    padding: "20px 24px",
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  body: {
    padding: "8px 0",
  },
  row: {
    display: "flex",
    alignItems: "center",
    padding: "16px 24px",
    gap: 16,
  },
  rowText: {
    flex: 1,
  },
  rowControl: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  label: {
    minWidth: 28,
    textAlign: "center",
    fontWeight: 500,
    fontSize: "0.8rem",
  },
}));

const TIPOS_NOTIFICACION = [
  {
    tipo: "WEBPAY_PAGO",
    titulo: "Pagos recibidos por WebPay",
    descripcion: (empleado) => (
      <>
        Se enviará un correo a <strong>{empleado.correo}</strong> cada vez que
        un cliente realice un pago por WebPay, ya sea desde el portal del
        cliente o reconciliado automáticamente.
      </>
    ),
  },
  {
    tipo: "CERTIFICADO_POR_VENCER",
    titulo: "Certificados por vencer",
    descripcion: (empleado) => (
      <>
        Se enviará un correo a <strong>{empleado.correo}</strong> cada vez que
        un certificado digital esté próximo a vencer.
      </>
    ),
  },
];

export default function NotificacionesEmpleados({ value, empleado, tabIndex }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { sucursal: sucursalObtenida } = useSelector(
    (state) => state.auth.user,
  );
  const sucursal = sucursalObtenida.refSucursal;

  const {
    data: notificaciones,
    isSuccess,
    isLoading: isLoadingQuery,
  } = empleadosApi.useGetNotificacionesQuery(
    { rutEmpleado: empleado.rut, sucursal },
    {
      skip: value !== tabIndex,
    },
  );

  const [toggleNotificacion, { isLoading: isToggling }] =
    empleadosApi.useToggleNotificacionMutation();

  const handleToggle = (tipo) => {
    AlertaModalV2({
      title: "¿Cambiar configuración?",
      text: "Se modificará la preferencia de notificación para este empleado.",
      confirmButtonText: "Sí, cambiar",
      icon: "question",
    })
      .fire()
      .then((result) => {
        if (result.isConfirmed) {
          toggleNotificacion({ rut: empleado.rut, tipo, sucursal })
            .unwrap()
            .then(() => {
              enqueueSnackbar("Configuración de notificación actualizada.", {
                variant: "success",
              });
            })
            .catch(() => {
              enqueueSnackbar("Error al actualizar la configuración.", {
                variant: "error",
              });
            });
        }
      });
  };

  if (value !== tabIndex) return null;

  // Helper para verificar si un tipo de notificación está activo en la base de datos
  const isActivo = (tipo) => {
    const notificacionesList = notificaciones?.data || [];
    const notif = notificacionesList.find((n) => n.tipo === tipo);
    return notif ? !!notif.activo : false;
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Notificaciones
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Configura qué alertas recibe este empleado por correo electrónico.
        </Typography>
      </Box>

      <Box className={classes.body}>
        {isLoadingQuery ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          isSuccess && (
            <>
              {TIPOS_NOTIFICACION.map((config) => {
                const activo = isActivo(config.tipo);

                return (
                  <React.Fragment key={config.tipo}>
                    <Box className={classes.row}>
                      <Box className={classes.rowText}>
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                          {config.titulo}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {config.descripcion(empleado)}
                        </Typography>
                      </Box>

                      <Box className={classes.rowControl}>
                        <Typography
                          className={classes.label}
                          color={!activo ? "primary" : "textSecondary"}
                        >
                          No
                        </Typography>

                        {isToggling ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Switch
                            checked={activo}
                            onChange={() => handleToggle(config.tipo)}
                            color="primary"
                            inputProps={{
                              "aria-label": `notificación ${config.tipo}`,
                            }}
                          />
                        )}

                        <Typography
                          className={classes.label}
                          color={activo ? "primary" : "textSecondary"}
                        >
                          Sí
                        </Typography>
                      </Box>
                    </Box>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </>
          )
        )}
      </Box>
    </Box>
  );
}
